import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faLock } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, LinearProgress, Typography } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { DeviceType } from '@/common/hooks/useDeviceDetection'
import { theme } from '@/theme/theme'
import TrainingCampaignTour from './TrainingCampaignTour'
import CywarenessLogo from '@/assets/images/logo-be-awere-stay-safe.svg'

type TrainingCampaignViewHeaderProps = {
  videoProgress: number
  handleContinue: () => void
  enableContinue: boolean
  hasQuiz?: boolean
  hasAnsweredQuiz: boolean
  deviceType: DeviceType
}

const TrainingCampaignViewHeader: FC<TrainingCampaignViewHeaderProps> = ({
  videoProgress,
  handleContinue,
  enableContinue = false,
  hasQuiz = true,
  hasAnsweredQuiz = false,
  deviceType,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <Box
      padding={deviceType === DeviceType.Mobile ? theme.spacing(2) : theme.spacing(4, 6, 2)}
      display="flex"
      flexDirection="column">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img
          alt={'Cywareness Logo'}
          src={CywarenessLogo}
          width={'auto'}
          style={{ maxHeight: deviceType === DeviceType.Mobile ? 25 : 50 }}
        />
        <TrainingCampaignTour />
      </Box>
      <Typography
        variant="body1"
        sx={{
          border: `1px solid ${theme.palette.cyan[500]}`,
          borderRadius: '10px',
          padding: deviceType === DeviceType.Mobile ? theme.spacing(1) : theme.spacing(2),
          margin: theme.spacing(2, 0),
          textAlign: 'center',
          fontSize: deviceType === DeviceType.Mobile ? 14 : 16,
        }}>
        {hasQuiz ? t('trainingCampaignPage.description') : t('trainingCampaignPage.descriptionNoQuiz')}
      </Typography>
      <Box display="flex" alignItems="center">
        <Box className={classes.progress}>
          {hasQuiz && (
            <Box className={classes.lock}>
              <FontAwesomeIcon icon={faLock as IconProp} />
            </Box>
          )}

          <LinearProgress
            color="secondary"
            variant="determinate"
            value={videoProgress}
            classes={{ colorSecondary: classes.progressBar }}
          />
        </Box>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleContinue}
          disabled={!enableContinue}
          size={deviceType === DeviceType.Mobile ? 'small' : 'medium'}>
          {hasAnsweredQuiz ? t('trainingCampaignPage.quizCompleted') : t('trainingCampaignPage.continue')}
        </Button>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    progress: {
      width: '100%',
      marginRight: theme.spacing(4),
      position: 'relative',
    },
    progressBar: {
      backgroundColor: theme.palette.grey[300],
      height: 10,
    },
    lock: {
      position: 'absolute',
      right: '10%',
      zIndex: 10,
      width: 40,
      height: 40,
      top: -16,
      borderRadius: '50%',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.cyan[500]}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    button: {
      minWidth: 'auto',
      whiteSpace: 'nowrap',
    },
  })
)
export default TrainingCampaignViewHeader
